import { TmElement } from '@thiememeulenhoff/lit-sdk';
import { html } from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedElements,
} from 'lit/decorators.js';
import { IconName } from '@thiememeulenhoff/types';
import { styles, tag } from './side-panel.styles';

import './side-panel-aside.component';

@customElement(tag)
export class SidePanelComponent extends TmElement {
  static override styles = styles;

  @query('main')
  main!: HTMLElement;

  @queryAssignedElements({ slot: 'end', selector: 'a-side-panel-aside' })
  endAsidePanels!: Array<HTMLElement>;

  @property({ type: String })
  icon?: IconName;

  @property({ type: String })
  label?: string;

  @property({ attribute: 'collapsable', type: Boolean, reflect: true })
  isCollapsable = false;

  #isOpen = true;

  @property({ type: Boolean, reflect: true })
  set open(val: boolean) {
    if (!val) {
      this.dispatchGlobalEvent({
        eventName: 'close',
        detail: val,
      });
    } else {
      this.dispatchGlobalEvent({
        eventName: 'open',
        detail: val,
      });
    }

    this.#isOpen = val;
    this.dispatchGlobalEvent({
      eventName: 'toggle',
      detail: val,
    });
  }

  get open() {
    return this.#isOpen;
  }

  override render() {
    return html`
      <slot name="start">
        <a-side-panel-aside
          ?open=${this.open}
          ?collapsable=${this.isCollapsable}
          icon=${this.icon}
          label=${this.label}
          @toggle=${this.panelToggle}
        >
          <div slot="title">${this.label}</div>
          <slot name="panel"></slot>
        </a-side-panel-aside>
      </slot>

      <main class="main" part="main">
        <slot></slot>
      </main>

      <slot name="end" @slotchange=${this.#endSlotChanged}></slot>
    `;
  }

  panelToggle(event: Event) {
    const custom = event as CustomEvent<boolean>;
    this.open = custom.detail;
  }

  close() {
    this.open = false;
  }

  toggle() {
    this.open = !this.open;
  }

  #endSlotChanged = () => {
    this.endAsidePanels.forEach((panel) => {
      // @ts-expect-error typing
      // eslint-disable-next-line no-param-reassign
      panel['position'] = 'end';
    });
  };

  override addEventListener<K extends keyof HTMLElementEventMap>(
    type: K,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listener: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions,
  ) {
    if (type === 'scroll' || type === 'scrollend') {
      return this.main.addEventListener(type, listener, options);
    }

    return super.addEventListener(type, listener, options);
  }

  override scrollTo(options?: ScrollToOptions | number, y?: number): void {
    if (typeof options === 'number' && typeof y === 'number') {
      this.main.scrollTo(options, y);
    } else {
      this.main.scrollTo(options as ScrollToOptions);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-side-panel': SidePanelComponent;
  }
}
